import {useEffect, useRef} from 'react';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useToast} from './Toast';
import {releaseInstant, IN_FLIGHT_RETRY_SECONDS} from '../lib/pendingTranscriptions';
import {pendingTranscriptionsSelector} from '../store/selectors/pendingTranscriptions.selectors';
import {transcriptionQuotaSelector} from '../store/selectors/usage.selectors';
import {startTranscription, fetchTranscriptions, pollTranscriptionStatus} from '../store/slices/transcriptionsThunks';
import {fetchTranscriptionUsage} from '../store/slices/usageThunks';
import {
  removePendingTranscription,
  reschedulePendingTranscription,
} from '../store/slices/pendingTranscriptionsSlice';

/** No network on a tick, so this can be frequent enough that a reset feels immediate. */
const TICK_MS = 15000;

/**
 * Releases the jobs held back by a session limit, and keeps the session clock honest.
 *
 * Mounted once in the protected layout, next to the transcription poller and for the same reason:
 * a queued job's release can be hours away, long after the user has left the workspace — and, once
 * the queue is restored from localStorage, after they have closed the tab and come back.
 *
 * **One release per tick, deliberately.** Firing the whole queue the moment the session opens would
 * spend the fresh budget in one burst and 429 every job after the first — which is exactly the
 * situation the queue exists to get the user out of. Releasing one at a time lets the server answer
 * for each, and a job refused again is simply pushed out to the new reset rather than dropped.
 *
 * Renders nothing: the workspace row is the UI, as it is for a running job.
 */
const PendingTranscriptionsContainer = () => {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();
  const pending = useAppSelector(pendingTranscriptionsSelector);
  const quota = useAppSelector(transcriptionQuotaSelector);

  // Held in refs so the interval below is created once per queue-empty/non-empty transition rather
  // than on every store update — the tick reads the current values itself.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const sessionExpiresAt = quota.session?.isActive ? quota.session.expiresAt : null;
  const sessionExpiryRef = useRef(sessionExpiresAt);
  sessionExpiryRef.current = sessionExpiresAt;

  // One release in flight at a time, across ticks. A slow 202 must not let the next tick send the
  // same job again — that would be two billed jobs for one queued entry.
  const isReleasingRef = useRef(false);

  const hasPending = pending.length > 0;

  useEffect(() => {
    const tick = async () => {
      const now = Date.now();

      // The session's own expiry, whether or not anything is queued: an active session that has
      // just lapsed leaves the header counting down past zero until something re-reads usage.
      const expiry = sessionExpiryRef.current;
      if (expiry && new Date(expiry).getTime() <= now) {
        sessionExpiryRef.current = null;
        dispatch(fetchTranscriptionUsage());
      }

      if (isReleasingRef.current) {
        return;
      }

      const due = pendingRef.current.find(entry => new Date(entry.releaseAt).getTime() <= now);

      if (!due) {
        return;
      }

      isReleasingRef.current = true;

      try {
        const result = await dispatch(startTranscription({
          audioId: due.audioId,
          outputFileName: due.outputFileName,
          ...(due.projectId ? {projectId: due.projectId} : {}),
          ...(due.languageCode ? {languageCode: due.languageCode} : {}),
        }));

        if (startTranscription.fulfilled.match(result)) {
          dispatch(removePendingTranscription(due.id));
          showToast(
            due.reason === 'in_flight'
              ? `${due.audioName} started — a slot freed up, so the queued job went through`
              : `${due.audioName} started — your session reset, so the queued job went through`,
            'success',
            6000
          );
          dispatch(fetchTranscriptionUsage());
          dispatch(fetchTranscriptions(due.projectId || 'all'));
          return;
        }

        const error = result.payload;

        if (!error || error.status === 0) {
          // Offline or the request never landed. Leave it queued and try on the next tick — the
          // job is still wanted and nothing about it has changed.
          return;
        }

        // This exact file is already running — most likely the user manually clicked Retry on it
        // at the same instant this tick tried to release it from the queue. Not a failure: the
        // queued entry has served its purpose, so drop it and adopt the job the server names
        // instead of rescheduling a release that will just 409 again.
        if (error.status === 409 && error.reason === 'duplicate_in_flight') {
          dispatch(removePendingTranscription(due.id));
          if (error.transcriptionId) {
            dispatch(pollTranscriptionStatus(error.transcriptionId));
          }
          return;
        }

        if (error.status === 429 && error.reason === 'session_limit_exceeded') {
          // Another job spent the new budget first. Push it out rather than surfacing a refusal the
          // user did not ask for — they already agreed to wait.
          dispatch(reschedulePendingTranscription({
            id: due.id,
            releaseAt: releaseInstant(error.sessionExpiresAt, error.retryAfterSeconds),
          }));
          return;
        }

        if (error.status === 429) {
          // too_many_in_flight, or the hourly limit. Both clear in minutes, so a short nudge out is
          // enough — no need to wait for another whole session.
          dispatch(reschedulePendingTranscription({
            id: due.id,
            releaseAt: new Date(
              now + (error.resetIn ?? error.retryAfterSeconds ?? IN_FLIGHT_RETRY_SECONDS) * 1000
            ).toISOString(),
          }));
          return;
        }

        // Anything else — the audio was deleted, the allowance ran out, the project changed hands.
        // Retrying on a timer would never fix it, so the entry is dropped and the user is told
        // once, naming the file they queued.
        dispatch(removePendingTranscription(due.id));
        showToast(`Could not start the queued ${due.audioName}: ${error.message}`, 'error', 8000);
      } finally {
        isReleasingRef.current = false;
      }
    };

    // Run once immediately: on a cold load the queue is restored from localStorage and a release
    // that came due while the tab was closed should not wait out a full tick.
    void tick();

    if (!hasPending && !sessionExpiryRef.current) {
      return;
    }

    const intervalId = setInterval(() => void tick(), TICK_MS);

    return () => clearInterval(intervalId);
  }, [hasPending, sessionExpiresAt, dispatch, showToast]);

  return null;
};

export default PendingTranscriptionsContainer;
